import { PublicApiAuthScope } from "@redotech/crdb-enums/common/public-api-auth-scope";
import { PartnerManagedAccountOrigin } from "@redotech/crdb-enums/partners/partner-managed-account-origin";
import { PartnerManagedAccountStatus } from "@redotech/crdb-enums/partners/partner-managed-account-status";
import { PartnerStatus } from "@redotech/crdb-enums/partners/partner-status";
import { PartnerUserStatus } from "@redotech/crdb-enums/partners/partner-user-status";
import * as z from "zod";

export type PartnerStatusValue = `${PartnerStatus}`;
export const partnerStatusSchema: z.ZodType<
  PartnerStatusValue,
  PartnerStatusValue
> = z.enum(PartnerStatus);

export type PartnerUserStatusValue = `${PartnerUserStatus}`;
export const partnerUserStatusSchema: z.ZodType<
  PartnerUserStatusValue,
  PartnerUserStatusValue
> = z.enum(PartnerUserStatus);

type StoreOrigin = `${PartnerManagedAccountOrigin}`;
const storeOriginSchema: z.ZodType<StoreOrigin, StoreOrigin> = z.enum(
  PartnerManagedAccountOrigin,
);

type StoreLinkStatus = `${PartnerManagedAccountStatus}`;
const storeLinkStatusSchema: z.ZodType<StoreLinkStatus, StoreLinkStatus> =
  z.enum(PartnerManagedAccountStatus);

type ApiScope = `${PublicApiAuthScope}`;
const apiScopeSchema: z.ZodType<ApiScope, ApiScope> =
  z.enum(PublicApiAuthScope);

const INT8_MAX = 9_223_372_036_854_775_807n;
const INT8_PATTERN = /^\d{1,19}$/;

/** Partner ids are int8, sent as decimal strings. */
export const partnerIdSchema = z
  .string()
  .regex(INT8_PATTERN)
  .refine((id) => INT8_PATTERN.test(id) && BigInt(id) <= INT8_MAX);

export const partnerSummarySchema = z.object({
  id: z.string(),
  name: z.string(),
  status: partnerStatusSchema,
  storeCount: z.number().int(),
  userCount: z.number().int(),
  createdBy: z.string(),
  createdAt: z.iso.datetime(),
});
export type PartnerSummary = z.infer<typeof partnerSummarySchema>;

export const partnerUserSchema = z.object({
  id: z.string(),
  email: z.string(),
  name: z.string().nullable(),
  status: partnerUserStatusSchema,
  createdBy: z.string(),
  createdAt: z.iso.datetime(),
});
export type PartnerUser = z.infer<typeof partnerUserSchema>;

/** A store the partner manages, or once managed if the link is revoked. */
export const partnerStoreSchema = z.object({
  teamId: z.string(),
  name: z.string(),
  storeUrl: z.string().nullable(),
  origin: storeOriginSchema,
  status: storeLinkStatusSchema,
  scopes: z.array(apiScopeSchema),
  createdAt: z.iso.datetime(),
});
export type PartnerStore = z.infer<typeof partnerStoreSchema>;

/** An unrevoked partner API token. Its secret is only returned on creation. */
export const partnerApiTokenSchema = z.object({
  id: z.string(),
  name: z.string(),
  tokenPrefix: z.string(),
  tokenSuffix: z.string(),
  createdBy: z.string(),
  createdAt: z.iso.datetime(),
  lastUsedAt: z.iso.datetime().nullable(),
});
export type PartnerApiToken = z.infer<typeof partnerApiTokenSchema>;
